import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { 
  ArrowLeft, 
  Download, 
  Send, 
  CheckCircle, 
  XCircle,
  Calendar,
  Hash,
  Edit,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import MobileLayout from '../layouts/MobileLayout';
import { Service, serviceApiService } from '../../services/serviceApi';
import { Pack, packsApiService } from '../../services/packsApi';
import { companyApiService, CompanyProfile } from '../../services/companyApi';
import { useAuth } from '../../components/AuthProvider';
import { purchaseRequestApiService, PurchaseRequestItem } from '../../services/purchaseRequestApi';

interface DevisItem {
  service?: Service;
  pack?: Pack;
  type: 'service' | 'pack';
  quantity: number;
  pages?: number;
  description?: string;
}

interface ClientInfo {
  name: string;
  email: string;
  phone: string;
  company?: string;
  address?: string;
}

const MobileFactureProformaPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const invoiceRef = useRef<HTMLDivElement>(null);
  
  // Fonction pour transformer les items de la base de données en format utilisable
  const transformPurchaseRequestItems = async (dbItems: any[]): Promise<DevisItem[]> => {
    const transformedItems: DevisItem[] = [];

    for (const dbItem of dbItems) {
      console.log('🔄 Transformation item:', dbItem);
      
      if (dbItem.type === 'service' && dbItem.service) {
        try {
          // Récupérer les détails du service
          const service = await serviceApiService.getService(dbItem.service.toString());
          transformedItems.push({
            type: 'service' as const,
            service: {
              ...service,
              name: dbItem.service_name || service.name, // Utiliser le nom de la facture ou le nom par défaut
              price: parseFloat(dbItem.unit_price) || service.price // Utiliser le prix de la facture
            },
            pack: undefined,
            quantity: dbItem.quantity || 1,
            pages: dbItem.pages,
            description: dbItem.description || service.description
          });
          console.log('✅ Service transformé:', dbItem.service_name, 'Prix:', dbItem.unit_price);
        } catch (error) {
          console.error('Erreur lors du chargement du service:', dbItem.service, error);
          // Fallback: créer un item basique avec les données disponibles
          transformedItems.push({
            type: 'service' as const,
            service: {
              id: dbItem.service.toString(),
              name: dbItem.service_name || 'Service',
              price: parseFloat(dbItem.unit_price) || 0,
              description: dbItem.description || '',
              price_type: 'fixed' as const,
              unit: undefined,
              is_active: true,
              price_display: `${parseFloat(dbItem.unit_price) || 0} F CFA`,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            },
            pack: undefined,
            quantity: dbItem.quantity || 1,
            pages: dbItem.pages,
            description: dbItem.description || ''
          });
        }
      } else if (dbItem.type === 'pack' && dbItem.pack) {
        try {
          // Récupérer les détails du pack
          const pack = await packsApiService.getPack(dbItem.pack.toString());
          transformedItems.push({
            type: 'pack' as const,
            service: undefined,
            pack: {
              ...pack,
              name: dbItem.pack_name || pack.name, // Utiliser le nom de la facture
              total_price: parseFloat(dbItem.unit_price) || pack.total_price // Utiliser le prix de la facture
            },
            quantity: dbItem.quantity || 1,
            pages: undefined,
            description: dbItem.description || pack.description
          });
          console.log('✅ Pack transformé:', dbItem.pack_name, 'Prix:', dbItem.unit_price);
        } catch (error) {
          console.error('Erreur lors du chargement du pack:', dbItem.pack, error);
          // Fallback: créer un item basique avec les données disponibles
          transformedItems.push({
            type: 'pack' as const,
            service: undefined,
            pack: {
              id: dbItem.pack.toString(),
              name: dbItem.pack_name || 'Pack',
              total_price: parseFloat(dbItem.unit_price) || 0,
              description: dbItem.description || '',
              payment_facility: undefined,
              is_active: true,
              services: [],
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            },
            quantity: dbItem.quantity || 1,
            pages: undefined,
            description: dbItem.description || ''
          });
        }
      }
    }

    return transformedItems;
  };
  
  const [items, setItems] = useState<DevisItem[]>([]);
  const [clientInfo, setClientInfo] = useState<ClientInfo>({
    name: '',
    email: '',
    phone: '',
    company: '',
    address: ''
  });
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [discount, setDiscount] = useState(0);
  const [tva, setTva] = useState(0);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [invoiceStatus, setInvoiceStatus] = useState<string>('pending');
  const [purchaseRequestId, setPurchaseRequestId] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [location.state, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Charger le profil entreprise
      const profile = await companyApiService.getCompanyProfile();
      setCompanyProfile(profile);
      if (profile.tva_rate) {
        setTva(Number(profile.tva_rate) || 0);
      }

      // Charger les données de la facture
      if (location.state?.purchaseRequestId) {
        const prId = location.state.purchaseRequestId;
        setPurchaseRequestId(prId);
        
        // Utiliser l'API appropriée selon le type d'utilisateur
        let pr;
        if (user?.isAdmin) {
          console.log('👤 Admin - Chargement avec API admin');
          pr = await purchaseRequestApiService.getPurchaseRequestAdmin(prId.toString());
        } else {
          console.log('👤 Client - Chargement avec API client (toutes les factures utilisateur)');
          // Pour les clients, récupérer toutes leurs factures et trouver celle demandée
          const userInvoices = await purchaseRequestApiService.getUserPurchaseRequests();
          console.log('🔍 Recherche facture ID:', prId, 'Type:', typeof prId);
          console.log('📋 Factures disponibles:', userInvoices.map(inv => ({ id: inv.id, type: typeof inv.id })));
          
          // Convertir prId en number pour la comparaison
          const targetId = typeof prId === 'string' ? parseInt(prId) : prId;
          pr = userInvoices.find(invoice => invoice.id === targetId);
          
          if (!pr) {
            throw new Error(`Facture ${prId} non trouvée dans vos factures. IDs disponibles: ${userInvoices.map(inv => inv.id).join(', ')}`);
          }
        }
        
        console.log('📋 Données facture récupérées:', pr);
        console.log('📦 Items reçus:', pr.items);
        console.log('💰 Total reçu:', pr.total_amount);
        console.log('🏷️ Remise reçue:', pr.discount_percentage);
        
        // Charger et transformer les items comme dans la version desktop
        if (pr.items && pr.items.length > 0) {
          console.log('🔄 Transformation des items...');
          const transformedItems = await transformPurchaseRequestItems(pr.items);
          console.log('✅ Items transformés:', transformedItems);
          setItems(transformedItems);
        } else {
          console.warn('⚠️ Aucun item trouvé dans la facture');
        }

        // Charger les informations de réduction
        if (pr.discount_percentage) {
          setDiscount(Number(pr.discount_percentage) || 0);
        }

        // Charger les informations client seulement si elles existent
        console.log('👤 Données client brutes:', {
          client_name: pr.client_name,
          client_email: pr.client_email,
          client_phone: pr.client_phone,
          client_company: pr.client_company,
          client_address: pr.client_address
        });

        if (pr.client_name || pr.client_email || pr.client_phone) {
          const clientData = {
            name: pr.client_name || '',
            email: pr.client_email || '',
            phone: pr.client_phone || '',
            company: pr.client_company || '',
            address: pr.client_address || ''
          };
          
          console.log('👤 Infos client chargées:', clientData);
          setClientInfo(clientData);
        } else {
          console.warn('⚠️ Aucune information client trouvée dans la facture');
        }

        console.log('📊 Statut facture:', pr.status);
        setInvoiceStatus(pr.status || 'pending');

        // Si c'est un admin qui consulte, activer le mode édition
        if (user?.isAdmin) {
          setEditMode(true);
        }
      } else if (location.state?.selectedServices) {
        setItems(location.state.selectedServices);
      }

      if (location.state?.promoDiscount) {
        setDiscount(Number(location.state.promoDiscount) || 0);
      }

      // Définir les infos utilisateur seulement si on n'a pas de facture existante
      if (user && !location.state?.purchaseRequestId && !purchaseRequestId) {
        console.log('👤 Utilisation des infos utilisateur par défaut');
        setClientInfo({
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
          phone: user.phone || '',
          company: user.company || '',
          address: ''
        });
      }
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateSubtotal = () => {
    return items.reduce((total, item) => {
      if (item.type === 'service' && item.service) {
        const basePrice = item.service.price || 0;
        if (item.pages) {
          return total + (basePrice * item.pages * item.quantity);
        }
        return total + (basePrice * item.quantity);
      }
      if (item.type === 'pack' && item.pack) {
        return total + (item.pack.total_price * item.quantity);
      }
      return total;
    }, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discountAmount = (subtotal * discount) / 100;
    const afterDiscount = subtotal - discountAmount;
    const tvaAmount = (afterDiscount * tva) / 100;
    return {
      subtotal,
      discountAmount,
      tvaAmount,
      total: afterDiscount + tvaAmount
    };
  };

  const generatePDF = async () => {
    if (!invoiceRef.current) return;
    
    try {
      setDownloadingPDF(true);
      
      const canvas = await html2canvas(invoiceRef.current, {
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;
      
      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      pdf.save(`facture_${purchaseRequestId || 'proforma'}.pdf`);
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      alert('Erreur lors de la génération du PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Fonction pour renvoyer la facture révisée (admin uniquement)
  const sendRevisedInvoice = async () => {
    if (!user?.isAdmin) {
      alert('Seuls les administrateurs peuvent renvoyer des factures révisées');
      return;
    }

    try {
      setSendingInvoice(true);

      const requestItems: PurchaseRequestItem[] = items.map(item => {
        let unitPrice = 0;
        let totalPrice = 0;
        
        if (item.type === 'service' && item.service) {
          const basePrice = item.service.price || 0;
          unitPrice = basePrice;
          if (item.pages) {
            totalPrice = basePrice * item.pages * item.quantity;
          } else {
            totalPrice = basePrice * item.quantity;
          }
        } else if (item.type === 'pack' && item.pack) {
          unitPrice = item.pack.total_price;
          totalPrice = item.pack.total_price * item.quantity;
        }
        
        const requestItem: any = {
          type: item.type,
          quantity: item.quantity,
          unit_price: unitPrice,
          total_price: totalPrice,
          description: item.description || ''
        };

        if (item.type === 'service' && item.service) {
          requestItem.service_id = Number(item.service.id);
          requestItem.service_name = item.service.name;
          if (item.pages) {
            requestItem.pages = item.pages;
          }
        } else if (item.type === 'pack' && item.pack) {
          requestItem.pack_id = Number(item.pack.id);
          requestItem.pack_name = item.pack.name;
        }

        return requestItem;
      });

      const totals = calculateTotal();
      const hasVariablePricing = items.some(item => 
        item.type === 'service' && item.service?.price_type === 'from'
      );

      // Valider et formater le pourcentage de remise (max 5 chiffres: XXX.XX)
      let validDiscountPercentage = Math.min(99.99, Math.max(0, discount || 0));
      
      // S'assurer que c'est un nombre avec maximum 2 décimales
      validDiscountPercentage = Math.round(validDiscountPercentage * 100) / 100;
      
      // Convertir en string puis back en number pour éviter les problèmes de précision
      validDiscountPercentage = parseFloat(validDiscountPercentage.toFixed(2));
      
      const requestData = {
        status: 'revision' as const,
        subtotal: totals.subtotal,
        discount_percentage: validDiscountPercentage,
        discount_amount: totals.discountAmount,
        tva_percentage: tva || 0,
        tva_amount: totals.tvaAmount,
        total_amount: totals.total,
        has_variable_pricing: hasVariablePricing,
        client_name: clientInfo.name,
        client_email: clientInfo.email,
        client_phone: clientInfo.phone,
        client_company: clientInfo.company,
        client_address: clientInfo.address,
        items: requestItems
      };

      console.log('📤 Données à envoyer à l\'API:', JSON.stringify(requestData, null, 2));
      console.log('📋 Items formatés:', requestItems);

      if (purchaseRequestId) {
        await purchaseRequestApiService.updatePurchaseRequest(purchaseRequestId, requestData);
        alert('✅ Facture révisée et renvoyée avec succès !');
      } else {
        await purchaseRequestApiService.createPurchaseRequest(requestData);
        alert('✅ Facture révisée créée et envoyée avec succès !');
      }

      navigate('/demandesclients');
    } catch (error) {
      console.error('Erreur envoi facture révisée:', error);
      alert('Erreur lors de l\'envoi de la facture révisée');
    } finally {
      setSendingInvoice(false);
    }
  };

  const sendPurchaseRequest = async () => {
    try {
      setSendingRequest(true);
      
      const requestItems: PurchaseRequestItem[] = items.map(item => {
        let unitPrice = 0;
        let totalPrice = 0;
        
        if (item.type === 'service' && item.service) {
          const basePrice = item.service.price || 0;
          unitPrice = basePrice;
          if (item.pages) {
            totalPrice = basePrice * item.pages * item.quantity;
          } else {
            totalPrice = basePrice * item.quantity;
          }
        } else if (item.type === 'pack' && item.pack) {
          unitPrice = item.pack.total_price;
          totalPrice = item.pack.total_price * item.quantity;
        }
        
        return {
          type: item.type,
          service: item.type === 'service' && item.service ? Number(item.service.id) : undefined,
          pack: item.type === 'pack' && item.pack ? Number(item.pack.id) : undefined,
          quantity: item.quantity,
          unit_price: unitPrice,
          total_price: totalPrice,
          pages: item.pages,
          description: item.description
        };
      });

      const totals = calculateTotal();
      
      // Vérifier si le devis contient des prix variables
      const hasVariablePricing = items.some(item => 
        item.type === 'service' && item.service?.price_type === 'from'
      );
      
      // Valider et formater le pourcentage de remise (max 5 chiffres: XXX.XX)
      let validDiscountPercentage = Math.min(99.99, Math.max(0, discount || 0));
      
      // S'assurer que c'est un nombre avec maximum 2 décimales
      validDiscountPercentage = Math.round(validDiscountPercentage * 100) / 100;
      
      // Convertir en string puis back en number pour éviter les problèmes de précision
      validDiscountPercentage = parseFloat(validDiscountPercentage.toFixed(2));
      
      
      await purchaseRequestApiService.createPurchaseRequest({
        status: 'pending',
        subtotal: totals.subtotal,
        discount_percentage: validDiscountPercentage,
        discount_amount: totals.discountAmount,
        tva_percentage: tva || 0,
        tva_amount: totals.tvaAmount,
        total_amount: totals.total,
        client_name: clientInfo.name,
        client_email: clientInfo.email,
        client_phone: clientInfo.phone,
        client_company: clientInfo.company,
        client_address: clientInfo.address,
        has_variable_pricing: hasVariablePricing,
        items: requestItems
      });

      alert('✅ Demande envoyée avec succès ! Vous recevrez une réponse sous 24h.');
      navigate('/factures');
    } catch (error) {
      console.error('Erreur envoi:', error);
      alert('Erreur lors de l\'envoi de la demande');
    } finally {
      setSendingRequest(false);
    }
  };

  // Fonctions pour modifier les items (admin uniquement)
  const updateItemQuantity = (index: number, newQuantity: number) => {
    if (!user?.isAdmin || !editMode) return;
    if (newQuantity < 1) return;
    
    const newItems = [...items];
    newItems[index].quantity = newQuantity;
    setItems(newItems);
  };

  const updateItemPages = (index: number, newPages: number) => {
    if (!user?.isAdmin || !editMode) return;
    if (newPages < 1) return;
    
    const newItems = [...items];
    newItems[index].pages = newPages;
    setItems(newItems);
  };

  const removeItem = (index: number) => {
    if (!user?.isAdmin || !editMode) return;
    if (!window.confirm('Supprimer cet item ?')) return;
    
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  const updateClientInfo = (field: keyof typeof clientInfo, value: string) => {
    if (!user?.isAdmin || !editMode) return;
    
    setClientInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateDiscount = (newDiscount: number) => {
    if (!user?.isAdmin || !editMode) return;
    // Limiter à 99.99% maximum pour respecter la contrainte backend (5 chiffres max)
    if (newDiscount < 0 || newDiscount > 99.99) return;
    
    setDiscount(newDiscount);
  };

  const confirmInvoice = async () => {
    if (!purchaseRequestId) return;
    
    try {
      setSendingRequest(true);
      await purchaseRequestApiService.updatePurchaseRequestStatus(purchaseRequestId, 'confirmed');
      alert('✅ Facture confirmée avec succès !');
      setInvoiceStatus('confirmed');
    } catch (error) {
      console.error('Erreur confirmation:', error);
      alert('Erreur lors de la confirmation');
    } finally {
      setSendingRequest(false);
    }
  };

  const cancelInvoice = async () => {
    if (!purchaseRequestId) return;
    
    try {
      setSendingRequest(true);
      await purchaseRequestApiService.updatePurchaseRequestStatus(purchaseRequestId, 'cancelled');
      alert('❌ Facture annulée');
      setInvoiceStatus('cancelled');
    } catch (error) {
      console.error('Erreur annulation:', error);
      alert('Erreur lors de l\'annulation');
    } finally {
      setSendingRequest(false);
    }
  };

  const totals = calculateTotal();

  if (loading) {
    return (
      <MobileLayout>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement...</p>
          </div>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      {/* Header fixe avec boutons */}
      <div className="bg-gradient-to-br from-amber-500 via-amber-600 to-orange-600 px-4 py-4 sticky top-0 z-10 shadow-lg">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/90 hover:text-white mb-3"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm">Retour</span>
        </button>
        
        <h1 className="text-xl font-bold text-white mb-3">Facture Proforma</h1>
        
        {/* Boutons d'action */}
        <div className="space-y-2">
          {/* Première ligne - Boutons communs */}
          <div className="flex gap-2">
            {user?.isAdmin && (
              <button 
                onClick={() => setEditMode(!editMode)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 ${
                  editMode 
                    ? 'bg-orange-600 text-white' 
                    : 'bg-white text-orange-600'
                }`}
              >
                <Edit className="w-4 h-4" />
                {editMode ? 'Terminer' : 'Modifier'}
              </button>
            )}
            
            <button 
              onClick={generatePDF}
              disabled={downloadingPDF}
              className="flex-1 py-2 bg-white text-amber-600 rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Download className={`w-4 h-4 ${downloadingPDF ? 'animate-spin' : ''}`} />
              {downloadingPDF ? 'Génération...' : 'PDF'}
            </button>
          </div>

          {/* Deuxième ligne - Actions selon le rôle */}
          <div className="flex gap-2">
            {user?.isAdmin ? (
              <button 
                onClick={sendRevisedInvoice}
                disabled={sendingInvoice}
                className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Send className={`w-4 h-4 ${sendingInvoice ? 'animate-spin' : ''}`} />
                {sendingInvoice ? 'Envoi...' : 'Renvoyer facture'}
              </button>
            ) : invoiceStatus === 'revision' ? (
              <>
                <button 
                  onClick={confirmInvoice}
                  disabled={sendingRequest}
                  className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <CheckCircle className="w-4 h-4" />
                  Confirmer
                </button>
                <button 
                  onClick={cancelInvoice}
                  disabled={sendingRequest}
                  className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <XCircle className="w-4 h-4" />
                  Annuler
                </button>
              </>
            ) : invoiceStatus === 'confirmed' ? (
              <button 
                onClick={cancelInvoice}
                disabled={sendingRequest}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <XCircle className="w-4 h-4" />
                Annuler facture
              </button>
            ) : invoiceStatus === 'cancelled' ? (
              <button 
                onClick={confirmInvoice}
                disabled={sendingRequest}
                className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <CheckCircle className="w-4 h-4" />
                Confirmer facture
              </button>
            ) : (
              <button 
                onClick={sendPurchaseRequest}
                disabled={sendingRequest}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                Envoyer
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Messages de statut */}
      {invoiceStatus === 'confirmed' && (
        <div className="mx-4 mt-4 bg-green-50 border-l-4 border-green-400 p-3 rounded-r-lg">
          <div className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-green-800 font-semibold text-sm mb-1">
                ✅ Facture confirmée !
              </h3>
              <p className="text-green-700 text-xs">
                Un expert vous contactera bientôt.
              </p>
            </div>
          </div>
        </div>
      )}

      {invoiceStatus === 'cancelled' && (
        <div className="mx-4 mt-4 bg-red-50 border-l-4 border-red-400 p-3 rounded-r-lg">
          <div className="flex items-start gap-2">
            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-red-800 font-semibold text-sm mb-1">
                ❌ Facture annulée
              </h3>
              <p className="text-red-700 text-xs">
                Cette facture a été annulée.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Facture à télécharger */}
      <div ref={invoiceRef} className="mx-4 my-6 bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* En-tête facture */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4">
          <h2 className="text-xl font-bold text-white mb-1">FACTURE PROFORMA</h2>
          {purchaseRequestId && (
            <div className="flex items-center gap-2 text-white/90 text-xs">
              <Hash className="w-3 h-3" />
              <span>Réf: {purchaseRequestId}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-white/90 text-xs mt-1">
            <Calendar className="w-3 h-3" />
            <span>{new Date().toLocaleDateString('fr-FR')}</span>
          </div>
        </div>

        {/* Info entreprise et client */}
        <div className="p-4 border-b border-gray-200">
          <div className="grid grid-cols-2 gap-4 text-xs">
            {/* Entreprise */}
            <div>
              <h3 className="font-bold text-gray-900 mb-2">De:</h3>
              {companyProfile && (
                <div className="text-gray-600 space-y-1">
                  <p className="font-semibold text-gray-900">{companyProfile.name}</p>
                  <p>{companyProfile.email}</p>
                  <p>{companyProfile.phone}</p>
                  {companyProfile.address && <p className="text-[10px]">{companyProfile.address}</p>}
                </div>
              )}
            </div>

            {/* Client */}
            <div>
              <h3 className="font-bold text-gray-900 mb-2">À:</h3>
              <div className="text-gray-600 space-y-1">
                {editMode && user?.isAdmin ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={clientInfo.name}
                      onChange={(e) => updateClientInfo('name', e.target.value)}
                      placeholder="Nom du client"
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded font-semibold text-gray-900"
                    />
                    <input
                      type="text"
                      value={clientInfo.company}
                      onChange={(e) => updateClientInfo('company', e.target.value)}
                      placeholder="Entreprise"
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                    />
                    <input
                      type="email"
                      value={clientInfo.email}
                      onChange={(e) => updateClientInfo('email', e.target.value)}
                      placeholder="Email"
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                    />
                    <input
                      type="tel"
                      value={clientInfo.phone}
                      onChange={(e) => updateClientInfo('phone', e.target.value)}
                      placeholder="Téléphone"
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                    />
                  </div>
                ) : (
                  <>
                    {clientInfo.name && <p className="font-semibold text-gray-900">{clientInfo.name}</p>}
                    {clientInfo.company && <p className="text-[10px]">{clientInfo.company}</p>}
                    {clientInfo.email && <p>{clientInfo.email}</p>}
                    {clientInfo.phone && <p>{clientInfo.phone}</p>}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="p-4">
          <h3 className="font-bold text-gray-900 mb-3 text-sm">Prestations</h3>
          
          {items.map((item, index) => {
            const itemName = item.type === 'service' && item.service ? item.service.name : 
                            item.type === 'pack' && item.pack ? item.pack.name : 'Item';
            
            let displayQty = item.quantity;
            let unitPrice = 0;
            let total = 0;
            
            if (item.type === 'service' && item.service) {
              const basePrice = item.service.price || 0;
              if (item.pages) {
                displayQty = item.pages;
                unitPrice = basePrice;
                total = basePrice * item.pages * item.quantity;
              } else {
                displayQty = item.quantity;
                unitPrice = basePrice;
                total = basePrice * item.quantity;
              }
            } else if (item.type === 'pack' && item.pack) {
              unitPrice = item.pack.total_price;
              total = item.pack.total_price * item.quantity;
            }

            return (
              <div key={index} className="border-b border-gray-100 py-2 text-xs">
                <div className="flex justify-between items-start mb-1">
                  <span className="font-semibold text-gray-900 flex-1">{itemName}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-900 font-bold">{total.toLocaleString()} F</span>
                    {editMode && user?.isAdmin && (
                      <button
                        onClick={() => removeItem(index)}
                        className="w-5 h-5 flex items-center justify-center rounded bg-red-100 text-red-600 hover:bg-red-200"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center text-gray-500">
                  {editMode && user?.isAdmin ? (
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => updateItemQuantity(index, item.quantity - 1)}
                          className="w-5 h-5 flex items-center justify-center rounded bg-gray-100 text-gray-600 hover:bg-gray-200 text-xs font-bold"
                        >
                          −
                        </button>
                        <span className="w-6 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateItemQuantity(index, item.quantity + 1)}
                          className="w-5 h-5 flex items-center justify-center rounded bg-gray-100 text-gray-600 hover:bg-gray-200 text-xs font-bold"
                        >
                          +
                        </button>
                      </div>
                      {item.pages && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs">Pages:</span>
                          <button
                            onClick={() => updateItemPages(index, (item.pages || 1) - 1)}
                            className="w-5 h-5 flex items-center justify-center rounded bg-gray-100 text-gray-600 hover:bg-gray-200 text-xs font-bold"
                          >
                            −
                          </button>
                          <span className="w-6 text-center">{item.pages}</span>
                          <button
                            onClick={() => updateItemPages(index, (item.pages || 1) + 1)}
                            className="w-5 h-5 flex items-center justify-center rounded bg-gray-100 text-gray-600 hover:bg-gray-200 text-xs font-bold"
                          >
                            +
                          </button>
                        </div>
                      )}
                      <span>× {unitPrice.toLocaleString()} F</span>
                    </div>
                  ) : (
                    <span>{displayQty} × {unitPrice.toLocaleString()} F</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Totaux */}
        <div className="px-4 pb-4">
          <div className="border-t-2 border-gray-200 pt-3 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Sous-total</span>
              <span className="font-semibold">{totals.subtotal.toLocaleString()} FCFA</span>
            </div>
            
            {(discount > 0 || (editMode && user?.isAdmin)) && (
              <div className="flex justify-between text-green-600">
                {editMode && user?.isAdmin ? (
                  <div className="flex items-center gap-2">
                    <span>Réduction</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => updateDiscount(Math.max(0, discount - 1))}
                        className="w-5 h-5 flex items-center justify-center rounded bg-gray-100 text-gray-600 hover:bg-gray-200 text-xs font-bold"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        value={discount}
                        onChange={(e) => updateDiscount(Number(e.target.value))}
                        min="0"
                        max="99.99"
                        step="0.01"
                        className="w-12 px-1 py-0.5 text-xs border border-gray-300 rounded text-center"
                      />
                      <span className="text-xs">%</span>
                      <button
                        onClick={() => updateDiscount(Math.min(99.99, discount + 1))}
                        className="w-5 h-5 flex items-center justify-center rounded bg-gray-100 text-gray-600 hover:bg-gray-200 text-xs font-bold"
                      >
                        +
                      </button>
                    </div>
                  </div>
                ) : (
                  <span>Réduction ({discount}%)</span>
                )}
                <span className="font-semibold">-{totals.discountAmount.toLocaleString()} FCFA</span>
              </div>
            )}
            
            {tva > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">TVA ({tva}%)</span>
                <span className="font-semibold">{totals.tvaAmount.toLocaleString()} FCFA</span>
              </div>
            )}
            
            <div className="flex justify-between pt-2 border-t border-gray-200">
              <span className="text-lg font-bold text-gray-900">TOTAL</span>
              <span className="text-lg font-bold text-amber-600">{totals.total.toLocaleString()} FCFA</span>
            </div>
          </div>
        </div>

        {/* Note de paiement */}
        <div className="bg-amber-50 p-4 text-xs">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-amber-900 mb-1">Note importante</h4>
              <p className="text-amber-800">
                Cette facture proforma est valable 30 jours. Le paiement d'un acompte de 50% est requis avant le démarrage des travaux.
              </p>
            </div>
          </div>
        </div>
      </div>
    </MobileLayout>
  );
};

export default MobileFactureProformaPage;
